import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Col, Grid, Modal, Row } from 'rsuite';

import { choices } from '../../../constants/choices';
import { FilterModal } from '../../../containers/styled/alerts';
import { TertiaryButton } from '../../../containers/styled/buttons';
import {
  DateField,
  SelectPickerField
} from '../../../containers/styled/styled';
import { Label } from '../../../containers/styled/typography';
import { useDatabase } from '../../../context/database';
import { listFacility } from '../../../controllers/reports/CommonController';
import { parseDate } from '../../../utils/dates';
import { SelectDiv } from '../../generic/styled';

const messages = defineMessages({
  genderPlaceholder: {
    id: 'report.filter.donor.gender.placeholder',
    defaultMessage: 'Select Gender Type'
  },
  facilityPlaceholder: {
    id: 'report.filter.donor.facility.placeholder',
    defaultMessage: 'Select Facility'
  },
  bloodTypePlaceholder: {
    id: 'report.filter.donor.bloodType.placeholder',
    defaultMessage: 'Select Blood Type'
  },
  datePeriodPlaceholder: {
    id: 'report.filter.donor.datePeriod.placeholder',
    defaultMessage: 'Select Date'
  },
  maleShort: {
    id: 'report.filter.donor.m',
    defaultMessage: 'M'
  },
  male: {
    id: 'report.filter.donor.male',
    defaultMessage: 'Male'
  },
  female: {
    id: 'report.filter.donor.female',
    defaultMessage: 'Female'
  }
});

/**
 * Donor Filter screen
 *
 * Donor filter to display modal to filter data table.
 *
 * @component
 * @param {array} data              an array of objects representing the data to be displayed
 * @param {boolean} handleClose     handles modal close event
 * @param setFilteredData           sets filtered data to the data table
 * @param initialQuery              default data to the data table
 * @param setInitialQuery           sets initialQuery data to table before modal closes
 * @returns donor filter modal.
 *
 */
const DonorFilter = ({ data, handleClose, setFilteredData, initialQuery, setInitialQuery }) => {
  const { formatMessage } = useIntl();
  const db = useDatabase();
  const [facilityData, setFacilityData] = React.useState([]);
  const [query, setQuery] = React.useState({
    gender: '',
    facility: '',
    bloodType: '',
    datePeriod: []
  });

  React.useEffect(() => {
    listFacility(db).then((data) => {
      const docs = data
        .filter((entry) => entry.isActive) // filter out inactive entries
        .map((entry) => ({
          value: entry.name,
          label: entry.name
        }));
      setFacilityData(docs);
    });
  }, [db]);

  const filterTable = () => {
    const filteredData = data.filter(
      (entry) =>
        (!query.gender || entry.gender === query.gender) &&
        (!query.facility || entry.facility === query.facility) &&
        (!query.bloodType || entry.bloodType === query.bloodType) &&
        (query.datePeriod.length === 0 || (
          parseDate(entry.createdAt) >= parseDate(query.datePeriod[0]) &&
          parseDate(entry.createdAt) <= parseDate(query.datePeriod[1]))
        )
    );
    setFilteredData(filteredData);
    setInitialQuery(query);
    handleClose();
  };

  React.useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  return (
    <FilterModal onClose={handleClose} size='xs'>
      <Modal.Body style={{ marginLeft: 26, marginRight: 26 }}>
        <Grid fluid>
          <Row>
            <Col xs={24}>
              <Label>
                <FormattedMessage
                  id='report.filter.donor.gender'
                  defaultMessage='Gender'
                />
              </Label>
              <SelectDiv>
                <SelectPickerField
                  value={query.gender}
                  searchable={false}
                  className='select-md'
                  placeholder={formatMessage(messages.genderPlaceholder)}
                  data={choices.gender.map((data) => ({
                    label: data.name.defaultMessage,
                    value: data.name.defaultMessage
                  }))}
                  onChange={(value) =>
                    setQuery((state) => ({ ...state, gender: value }))}
                />
              </SelectDiv>
            </Col>
            <Col xs={24}>
              <Label>
                <FormattedMessage
                  id='report.filter.donor.facility'
                  defaultMessage='Facility'
                />
              </Label>
              <SelectDiv>
                <SelectPickerField
                  value={query.facility}
                  searchable={false}
                  className='select-md'
                  placeholder={formatMessage(messages.facilityPlaceholder)}
                  data={facilityData}
                  onChange={(value) =>
                    setQuery((state) => ({ ...state, facility: value }))}
                />
              </SelectDiv>
            </Col>
            <Col xs={24}>
              <Label>
                <FormattedMessage
                  id='report.filter.donor.bloodType'
                  defaultMessage='Blood Type'
                />
              </Label>
              <SelectDiv>
                <SelectPickerField
                  value={query.bloodType}
                  searchable={false}
                  className='select-md'
                  placeholder={formatMessage(messages.bloodTypePlaceholder)}
                  data={choices.bloodType.map((data) => ({
                    label: data.name.defaultMessage,
                    value: data.name.defaultMessage
                  }))}
                  onChange={(value) =>
                    setQuery((state) => ({ ...state, bloodType: value }))}
                />
              </SelectDiv>
            </Col>
            <Col xs={24}>
              <Label>
                <FormattedMessage
                  id='report.filter.donor.datePeriod'
                  defaultMessage='Date Period'
                />
              </Label>
              <SelectDiv>
                <DateField
                  value={query.datePeriod.length ? query.datePeriod : null}
                  className='select-md'
                  block
                  placeholder={formatMessage(messages.datePeriodPlaceholder)}
                  onClean={() => setQuery((state) => ({ ...state, datePeriod: [] }))}
                  onOk={(value) =>
                    setQuery((state) => ({ ...state, datePeriod: value }))}
                />
              </SelectDiv>
            </Col>
            <Col style={{ marginTop: 24 }} xs={24}>
              <TertiaryButton onClick={() => filterTable()}>
                <FormattedMessage
                  id='report.filter.button'
                  defaultMessage='Filter'
                />
              </TertiaryButton>
            </Col>
          </Row>
        </Grid>
      </Modal.Body>
    </FilterModal>
  );
};
export default DonorFilter;
